import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@nrwl/environment';
import { map, Observable } from 'rxjs';
import { MedicalNote } from './medical-note';
import { Treatment } from './treatment';
import { Employee } from './employee';

/**
 * Parameters for fetching PNE medical notes.
 */
export interface GetMedicalNotesParams {
  /**
   * Identifier of the customer.
   */
  readonly customerId: string;

  /**
   * Identifier of the treatment.
   */
  readonly treatmentId: string;
}

/**
 * Parameters for fetching treatment details.
 */
export interface GetTreatmentParams {
  /**
   * Identifier of the appointment.
   */
  readonly appointmentId: string;
}

/**
 * Parameters for fetching employee details.
 */
export interface GetEmployeeParams {
  /**
   * Identifier of the employee.
   */
  readonly employeeId: string;
}

@Injectable({ providedIn: 'root' })
export class MedicalNotesService {
  private readonly http = inject(HttpClient);

  /**
   * Fetches PNE medical notes of type "Other" for a specific customer and treatment.
   */
  public getMedicalNotes(params: GetMedicalNotesParams): Observable<readonly MedicalNote[]> {
    const { customerId, treatmentId } = params;

    const queryParams = { NoteType: 'Other', CustomerId: customerId, TreatmentId: treatmentId };

    return this.http
      .get<unknown>(`${environment.baseAPIUrlMicro}egress-proxy/pne/treatmentsoap`, {
        params: queryParams,
      })
      .pipe(map((response) => this.parseMedicalNotesResponse(response)));
  }

  /**
   * Fetches treatment details for a specific appointment.
   */
  public getTreatment(params: GetTreatmentParams): Observable<Treatment> {
    const { appointmentId } = params;

    const queryParams = { legacyids: 'true' };

    return this.http
      .get<unknown>(
        `${environment.baseAPIUrlMicro}egress-proxy/pne/appointments/${appointmentId}/treatment/`,
        {
        params: queryParams,
        },
      )
      .pipe(map((response) => this.parseTreatmentResponse(response)));
  }

  /**
   * Fetches employee details by its identifier.
   */
  public getEmployee(params: GetEmployeeParams): Observable<Employee> {
    const { employeeId } = params;

    return this.http
      .get<unknown>(`${environment.baseAPIUrlMicro}egress-proxy/pne/employees/${employeeId}`)
      .pipe(map((response) => this.parseEmployeeResponse(response)));
  }

  /**
   * Parses the treatmentsoap response array into normalized medical notes.
   */
  private parseMedicalNotesResponse(response: unknown): readonly MedicalNote[] {
    if (!Array.isArray(response)) {
      throw new Error('Invalid medical notes response.');
    }

    return response.map((item, index) => {
      if (!item || typeof item !== 'object') {
        throw new Error(`Invalid medical note payload at index ${index}.`);
      }

      const record = item as Record<string, unknown>;

      const noteId = record['Id'];
      const noteDate = record['NoteDate'];
      const noteText = record['Note'];
      const employeeId = record['EmployeeId'];

      if (typeof noteId !== 'string') {
        throw new Error(`Invalid medical note fields at index ${index}.`);
      }

      if (typeof noteDate !== 'string' || typeof noteText !== 'string') {
        throw new Error(`Invalid medical note fields at index ${index}.`);
      }

      if (typeof employeeId !== 'string' && typeof employeeId !== 'number') {
        throw new Error(`Invalid medical note employee at index ${index}.`);
      }

      const date = new Date(noteDate);

      if (Number.isNaN(date.getTime())) {
        throw new Error(`Invalid medical note date at index ${index}.`);
      }

      const normalizedEmployeeId = String(employeeId).trim();
      const normalizedText = String(noteText).trim();

      if (!normalizedEmployeeId || !normalizedText) {
        throw new Error(`Invalid medical note values at index ${index}.`);
      }

      return {
        id: noteId,
        date: date,
        employeeId: normalizedEmployeeId,
        text: normalizedText,
      };
    });
  }

  /**
   * Parses the treatment response into normalized structure.
   */
  private parseTreatmentResponse(response: unknown): Treatment {
    const id = this.extractTreatmentId(response);
    if (!id) {
      throw new Error('Invalid treatment response.');
    }
    return { id };
  }

  private extractTreatmentId(response: unknown): string | null {
    // New endpoint returns { treatmentId }, legacy responses may return arrays with Id
    if (response && typeof response === 'object' && !Array.isArray(response)) {
      const record = response as Record<string, unknown>;
      const treatmentId = record['treatmentId'] ?? record['TreatmentId'] ?? record['Id'];
      if (typeof treatmentId === 'string' || typeof treatmentId === 'number') {
        const val = String(treatmentId).trim();
        return val || null;
      }
    }

    if (Array.isArray(response) && response.length > 0) {
      const first = response[0] as Record<string, unknown>;
      const id = first['Id'] ?? first['treatmentId'] ?? first['TreatmentId'];
      if (typeof id === 'string' || typeof id === 'number') {
        const val = String(id).trim();
        return val || null;
      }
    }

    return null;
  }

  /**
   * Parses the employee response into normalized structure.
   */
  private parseEmployeeResponse(response: unknown): Employee {
    if (!response || typeof response !== 'object') {
      throw new Error(`Invalid employee payload.`);
    }

    const record = response as Record<string, unknown>;

    const id = record['EmployeeId'];
    const firstName = record['FirstName'];
    const lastName = record['LastName'];

    if (typeof id !== 'string') {
      throw new Error(`Invalid employee ID.`);
    }

    if (typeof firstName !== 'string') {
      throw new Error(`Invalid employee first name.`);
    }

    if (typeof lastName !== 'string') {
      throw new Error(`Invalid employee last name.`);
    }

    return { id, firstName, lastName };
  }
}
